/**
 * AI Agent Add Wizard — tabbed create flow for bot (WACalls AI Agent) campaigns.
 * Tabs: Campaign Information · Leads Selection · Dialing · Routing · AMD Detection
 */
import { useEffect, useMemo, useState } from "react";
import { CaretUpDown, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  normalizeCallRecording,
  validateCampaignForm,
  buildCreateCampaignBody,
  selectedOpensipsIds,
  selectedMediaServerIds,
} from "@/lib/campaignForm";
import FailoverServerMultiSelect from "@/components/FailoverServerMultiSelect";
import DialingScheduleFields from "@/components/DialingScheduleFields";
import CampaignDispositionFields from "@/components/CampaignDispositionFields";

const TABS = [
  { id: "info", label: "Campaign Information" },
  { id: "leads", label: "Leads Selection" },
  { id: "dialing", label: "Dialing" },
  { id: "routing", label: "Routing" },
  { id: "amd", label: "AMD Detection" },
];

/** Lead statuses that are not wrap-up dispositions but must stay dial-selectable. */
const LEAD_STATUS_CODES = [
  { code: "NEW", label: "New" },
  { code: "QUEUE", label: "Queue" },
  { code: "DIALING", label: "Dialing" },
];

function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Searchable multi-select (lead lists). */
function SmartMultiSelect({
  items,
  selectedIds,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No options",
  testid,
  getLabel = (item) => item.name || item.label || item.id,
  getId = (item) => item.id,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const byId = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(getId(it), it));
    return m;
  }, [items, getId]);

  const display = useMemo(() => {
    if (!selected.length) return placeholder;
    const names = selected.map((id) => getLabel(byId.get(id) || { id }));
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [selected, byId, placeholder, getLabel]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {(items || []).map((it) => {
                const id = getId(it);
                const on = selectedSet.has(id);
                return (
                  <CommandItem
                    key={id}
                    value={`${getLabel(it)} ${id}`}
                    data-testid={`${testid}-option-${id}`}
                    onSelect={() => toggle(id)}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{getLabel(it)}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AiAgentAddWizard({
  open,
  onOpenChange,
  value,
  onChange,
  options = {},
  optionsError: _optionsError,
  onInboundGroupsReload: _onInboundGroupsReload,
  saving = false,
  onSubmit,
  onBackToDialing,
  vmUploading = false,
  vmInputRef,
  onVmUploadClick,
  onVmFileChange,
}) {
  const v = value || {};
  const set = (patch) => onChange(patch);
  const [tab, setTab] = useState("info");
  const [dispositions, setDispositions] = useState([]);
  const [disposLoading, setDisposLoading] = useState(false);

  const {
    pdServers = [],
    assigned = [],
    mediaServers = [],
    leadLists = [],
    vendors = [],
    timezones = [],
  } = options;

  const recordingOn = normalizeCallRecording(v.callRecording, v.recordingEnabled) === "always";
  const tabIndex = TABS.findIndex((t) => t.id === tab);

  useEffect(() => {
    if (open) setTab("info");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setDisposLoading(true);
      try {
        const { data } = await api.get("/customer-settings/dispositions");
        if (cancelled) return;
        const system = Array.isArray(data?.system) ? data.system : [];
        const custom = Array.isArray(data?.custom) ? data.custom : [];
        const seen = new Set();
        const rows = [];
        for (const row of [...LEAD_STATUS_CODES, ...system, ...custom]) {
          const code = String(row.code || "").toUpperCase();
          if (!code || code === "DNC" || seen.has(code)) continue;
          seen.add(code);
          rows.push({
            code,
            label: row.label || code,
            source: row.source || (LEAD_STATUS_CODES.some((x) => x.code === code) ? "lead" : "system"),
          });
        }
        setDispositions(rows);
      } catch {
        if (!cancelled) {
          setDispositions(
            LEAD_STATUS_CODES.concat([
              { code: "ANSWERED", label: "Answered" },
              { code: "BUSY", label: "Busy" },
              { code: "NOANSWER", label: "No Answer" },
              { code: "FAILED", label: "Failed" },
              { code: "COMPLETED", label: "Completed" },
              { code: "CALLBACK", label: "Callback" },
            ]),
          );
        }
      } finally {
        if (!cancelled) setDisposLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggleDialStatus = (code, checked) => {
    const cur = Array.isArray(v.dialStatuses) ? v.dialStatuses : [];
    set({
      dialStatuses: checked
        ? (cur.includes(code) ? cur : [...cur, code])
        : cur.filter((x) => x !== code),
    });
  };

  const goNext = () => {
    if (tabIndex < TABS.length - 1) setTab(TABS[tabIndex + 1].id);
  };
  const goBack = () => {
    if (tabIndex > 0) setTab(TABS[tabIndex - 1].id);
  };

  const submit = async () => {
    const check = validateCampaignForm(v, { pdServerCount: pdServers.length });
    if (!check.ok) {
      toast.error(check.error);
      const err = check.error || "";
      if (!(v.name || "").trim()
        || v.dialRate === "" || v.dialRate == null
        || err.includes("Dial Rate")
        || err.includes("Max call duration")) {
        setTab("info");
      } else if (err.includes("Dialing")) {
        setTab("dialing");
      } else if (
        (!((v.pdServerIds || []).length || v.pdServerId) && pdServers.length !== 1)
        || !v.vendorId || v.vendorId === "__none__"
        || selectedOpensipsIds(v).length === 0
        || selectedMediaServerIds(v).length === 0
      ) {
        setTab("routing");
      } else if (v.amdEnabled && v.amdAction === "voicemail" && !v.vmAudioId) {
        setTab("amd");
      }
      return;
    }
    await onSubmit?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] flex flex-col"
        data-testid="ai-agent-wizard"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Add AI Agent campaign</DialogTitle>
          <DialogDescription>
            Configure campaign information, leads, dialing schedule, routing, and AMD detection.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList
            className="w-full h-auto flex flex-wrap justify-start gap-1 shrink-0"
            data-testid="ai-agent-wizard-tabs"
          >
            {TABS.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className="text-xs sm:text-sm"
                data-testid={`ai-agent-wizard-tab-${t.id}`}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto py-3 pr-1 -mr-1 min-h-[280px]">
            {/* ── Campaign Information ─────────────────────────── */}
            <TabsContent value="info" className="mt-0 space-y-4" data-testid="ai-agent-wizard-panel-info">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Campaign name" required>
                  <Input
                    data-testid="ai-agent-wizard-name"
                    value={v.name || ""}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="e.g. Sales Outbound"
                  />
                </Field>
                <Field
                  label="Dial Rate"
                  required
                  hint="Calls per second (CPS)."
                >
                  <Input
                    data-testid="ai-agent-wizard-dial-rate"
                    type="number"
                    min="0.1"
                    step="0.1"
                    required
                    value={v.dialRate ?? "1"}
                    onChange={(e) => set({ dialRate: e.target.value })}
                    placeholder="1"
                  />
                </Field>
              </div>

              <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
                <div>
                  <Label className="text-sm">Call recording</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Record when the customer is connected.
                  </p>
                </div>
                <Switch
                  data-testid="ai-agent-wizard-recording"
                  checked={recordingOn}
                  onCheckedChange={(on) => set({
                    callRecording: on ? "always" : "never",
                    ...(on ? {} : { recordingStartDelaySec: "" }),
                  })}
                />
              </div>

              {recordingOn && (
                <Field
                  label="Start Recording after (sec)"
                  hint="Leave empty to start as soon as the call is connected."
                >
                  <Input
                    data-testid="ai-agent-wizard-recording-delay"
                    type="number"
                    min="1"
                    max="3600"
                    value={v.recordingStartDelaySec || ""}
                    onChange={(e) => set({ recordingStartDelaySec: e.target.value })}
                    placeholder=""
                  />
                </Field>
              )}

              <Field
                label="Max Call Duration (sec)"
                hint="Caps the AI bot ↔ customer call. Leave blank for no limit. Not applied after transfer to an agent."
              >
                <Input
                  data-testid="ai-agent-wizard-max-duration"
                  type="number"
                  min="1"
                  max="3600"
                  value={v.maxDur || ""}
                  onChange={(e) => set({ maxDur: e.target.value })}
                  placeholder="Unlimited"
                />
              </Field>
            </TabsContent>

            {/* ── Leads Selection ──────────────────────────────── */}
            <TabsContent value="leads" className="mt-0 space-y-4" data-testid="ai-agent-wizard-panel-leads">
              <Field
                label="Select Lists"
                hint="Lead lists this campaign may dial."
              >
                <SmartMultiSelect
                  items={leadLists}
                  selectedIds={v.listIds || []}
                  onChange={(ids) => set({ listIds: ids })}
                  placeholder="Select list(s)…"
                  searchPlaceholder="Search lists…"
                  emptyText="No lists yet — create them under Lists."
                  testid="ai-agent-wizard-lists"
                  getLabel={(l) => (
                    l.lead_count != null ? `${l.name} (${l.lead_count})` : (l.name || l.id)
                  )}
                />
              </Field>

              <CampaignDispositionFields
                value={v}
                onChange={set}
                testidPrefix="ai-agent-wizard-dispos"
              />
            </TabsContent>

            {/* ── Dialing ──────────────────────────────────────── */}
            <TabsContent value="dialing" className="mt-0 space-y-4" data-testid="ai-agent-wizard-panel-dialing">
              <DialingScheduleFields
                value={v}
                onChange={set}
                timezones={timezones}
                testidPrefix="ai-agent-wizard-dialing"
              />
            </TabsContent>

            {/* ── Routing ──────────────────────────────────────── */}
            <TabsContent value="routing" className="mt-0 space-y-4" data-testid="ai-agent-wizard-panel-routing">
              <Field
                label="Select PD servers"
                required={pdServers.length !== 1}
                hint="First selected is Primary; others are backups if the primary is down or at capacity."
              >
                <FailoverServerMultiSelect
                  servers={pdServers}
                  selectedIds={
                    (v.pdServerIds || []).length
                      ? v.pdServerIds
                      : (v.pdServerId ? [v.pdServerId] : [])
                  }
                  onChange={(ids) => set({ pdServerIds: ids, pdServerId: ids[0] || "" })}
                  testid="ai-agent-wizard-pd-servers"
                  emptyText="No PD servers assigned to this account."
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Telephony IP"
                  required
                  hint="First selected is Primary; others are backups."
                >
                  <FailoverServerMultiSelect
                    servers={assigned}
                    selectedIds={selectedOpensipsIds(v)}
                    onChange={(ids) => set({ opensipsIds: ids, opensipsId: ids[0] || "__none__" })}
                    testid="ai-agent-wizard-telephony-ip"
                    emptyText="No Telephony IP servers assigned to this account."
                  />
                </Field>
                <Field
                  label="Media server"
                  required
                  hint="First selected is Primary; others are backups."
                >
                  <FailoverServerMultiSelect
                    servers={mediaServers}
                    selectedIds={selectedMediaServerIds(v)}
                    onChange={(ids) => set({ mediaServerIds: ids, mediaServerId: ids[0] || "" })}
                    testid="ai-agent-wizard-media-servers"
                    emptyText="No media servers assigned to this account."
                  />
                </Field>
              </div>

              <Field
                label="Vendor route"
                required
                hint="Required. Campaign-selected vendor only — no outbound vendor failover."
              >
                <Select
                  value={v.vendorId && v.vendorId !== "__none__" ? v.vendorId : undefined}
                  onValueChange={(x) => set({ vendorId: x })}
                >
                  <SelectTrigger data-testid="ai-agent-wizard-vendor">
                    <SelectValue placeholder="Select vendor route…" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((x) => (
                      <SelectItem key={x.id} value={x.id}>{x.name} · {x.host}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {vendors.length === 0 && (
                  <p className="text-xs text-destructive" data-testid="ai-agent-wizard-vendor-empty">
                    No active vendor routes — create one under Routing → Vendors.
                  </p>
                )}
              </Field>
            </TabsContent>

            {/* ── AMD Detection ────────────────────────────────── */}
            <TabsContent value="amd" className="mt-0 space-y-4" data-testid="ai-agent-wizard-panel-amd">
              <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2">
                <div>
                  <Label className="text-sm">AMD Detection</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Classify human vs answering machine on connect.
                  </p>
                </div>
                <Switch
                  data-testid="ai-agent-wizard-amd-enabled"
                  checked={!!v.amdEnabled}
                  onCheckedChange={(x) => set({ amdEnabled: x })}
                />
              </div>

              {v.amdEnabled && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Decision window (ms)">
                    <Input
                      data-testid="ai-agent-wizard-amd-timeout"
                      type="number"
                      min="1000"
                      max="8000"
                      step="100"
                      value={v.amdTimeout || "3200"}
                      onChange={(e) => set({ amdTimeout: e.target.value })}
                    />
                  </Field>

                  <Field label="On voicemail">
                    <Select
                      value={
                        v.amdAction === "voicemail" || v.amdAction === "log_only"
                          ? v.amdAction
                          : "hangup"
                      }
                      onValueChange={(x) => set({ amdAction: x })}
                    >
                      <SelectTrigger data-testid="ai-agent-wizard-amd-action">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hangup">Hangup</SelectItem>
                        <SelectItem value="voicemail">Leave voicemail</SelectItem>
                        <SelectItem value="log_only">Log only</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Voicemail Max (ms)">
                    <Input
                      data-testid="ai-agent-wizard-amd-greeting-max"
                      type="number"
                      min="2000"
                      max="12000"
                      step="100"
                      value={v.amdGreetingMax || "4500"}
                      onChange={(e) => set({ amdGreetingMax: e.target.value })}
                    />
                  </Field>

                  {v.amdAction === "voicemail" && (
                    <Field label="Voicemail drop audio" required className="sm:col-span-2">
                      <input
                        ref={vmInputRef}
                        type="file"
                        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm"
                        hidden
                        onChange={onVmFileChange}
                        data-testid="ai-agent-wizard-vm-file-input"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={vmUploading}
                          onClick={onVmUploadClick}
                          data-testid="ai-agent-wizard-vm-upload"
                        >
                          {vmUploading ? "Uploading…" : (v.vmAudioId ? "Replace audio" : "Upload audio")}
                        </Button>
                        {v.vmAudioId ? (
                          <span className="text-xs text-emerald-600 truncate" data-testid="ai-agent-wizard-vm-filename">
                            {v.vmFilename || "Voicemail audio attached"}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No file uploaded yet</span>
                        )}
                      </div>
                    </Field>
                  )}
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="shrink-0 border-t pt-3 gap-2 flex-wrap sm:justify-between">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => onBackToDialing?.()}
              data-testid="ai-agent-wizard-back-dialing"
            >
              Change dialing type
            </Button>
          </div>
          <div className="flex gap-2 ml-auto">
            {tabIndex > 0 && (
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                onClick={goBack}
                data-testid="ai-agent-wizard-back"
              >
                Back
              </Button>
            )}
            {tabIndex < TABS.length - 1 ? (
              <Button
                type="button"
                className="rounded-lg"
                onClick={goNext}
                data-testid="ai-agent-wizard-next"
              >
                Next
              </Button>
            ) : (
              <Button
                type="button"
                className="rounded-lg"
                disabled={saving}
                onClick={submit}
                data-testid="ai-agent-wizard-submit"
              >
                {saving ? "Creating…" : "Create campaign"}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Build create body — same as shared helper (kept for tests / clarity). */
export function buildAiAgentCreateBody(form) {
  return buildCreateCampaignBody({ ...form, dialingType: "bot" });
}
